/**
 * Decodes the JWT payload from a Base64URL string into a UTF-8 compatible JavaScript object.
 * This function uses the modern TextDecoder API for robust UTF-8 handling.
 * NOTE: This only decodes the token; it does NOT verify the signature for security.
 * * @param {string} token - The JWT string to be decoded (e.g., header.payload.signature)
 * @returns {object | null} - The decoded payload object or null if an error occurs (invalid format, decoding failure)
 */
export function decodeJwt(token: string){
  if (!token) {
    return null;
  }

  try {
    const parts = token.split('.');
    
    // A JWT must consist of three parts: Header.Payload.Signature
    if (parts.length !== 3) {
        return null; // Invalid JWT format
    }

    let base64 = parts[1]; // The Payload is the second part

    // 1. Base64 URL Safe fix: Replace '-' with '+' and '_' with '/'
    // This converts the Base64URL format back to standard Base64
    base64 = base64.replace(/-/g, '+').replace(/_/g, '/');

    // 2. Add padding ('=') characters if missing, required for atob() to work correctly
    while (base64.length % 4) {
      base64 += '=';
    }

    // --- UTF-8 Decoding ---

    // 3. Decode Base64 string into a binary string using atob()
    const binaryString = atob(base64);

    // 4. Convert the binary string into a byte array (Uint8Array)
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      // charCodeAt gets the byte value
      bytes[i] = binaryString.charCodeAt(i);
    }

    // 5. Use TextDecoder to interpret the byte array as a UTF-8 string
    // This step correctly handles multi-byte characters (like Turkish or CJK characters)
    const decoder = new TextDecoder('utf-8');
    const jsonPayload = decoder.decode(bytes);

    // 6. Parse the JSON string into a JavaScript object
    return JSON.parse(jsonPayload);

  } catch (error) {
    // Handle any errors during splitting, Base64 decoding, or JSON parsing
    console.error("Error decoding JWT:", error);
    return null;
  }
}